import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { motion, AnimatePresence } from 'framer-motion';
import {
    NotePencil,
    MapPin,
    User,
    Package,
    UsersThree,
    Sparkle,
    Lock,
    LockOpen,
    DotsThreeVertical,
    Trash,
    Icon
} from '@phosphor-icons/react';
import { IdeaCard as IdeaCardType, CardFocus, AttachedEntity } from '../../types';
import { EntityChip } from './EntityChip';

// ═══════════════════════════════════════════════════════════════════════════════
// FOCUS CONFIG - Premiere Pro inspired, muted colors
// ═══════════════════════════════════════════════════════════════════════════════
// Using project palette: #1E1E20, #121212, #2A2A2C, #3F4448, #7775F8
const FOCUS_CONFIG: Record<CardFocus, { icon: Icon; color: string; label: string; bg: string; border: string; accent: string }> = {
    note: {
        icon: NotePencil,
        color: 'text-gray-400',
        bg: 'bg-iron/30',
        border: 'border-iron/50',
        accent: 'var(--color-gray-500)',
        label: 'Note'
    },
    scene: {
        icon: MapPin,
        color: 'text-place-soft',
        bg: 'bg-place/10',
        border: 'border-place/30',
        accent: 'var(--color-place)',
        label: 'Scene'
    },
    character: {
        icon: User,
        color: 'text-entity-soft',
        bg: 'bg-entity/10',
        border: 'border-entity/30',
        accent: 'var(--color-entity)',
        label: 'Character'
    },
    prop: {
        icon: Package,
        color: 'text-thing-soft',
        bg: 'bg-thing/10',
        border: 'border-thing/30',
        accent: 'var(--color-thing)',
        label: 'Prop'
    },
    prop_significance: {
        icon: Sparkle,
        color: 'text-significance-soft',
        bg: 'bg-significance/10',
        border: 'border-significance/30',
        accent: 'var(--color-significance)',
        label: 'Significance'
    },
    relationship: {
        icon: UsersThree,
        color: 'text-relationship-soft',
        bg: 'bg-relationship/10',
        border: 'border-relationship/30',
        accent: 'var(--color-relationship)',
        label: 'Relationship'
    }
};

interface IdeaCardProps {
    card: IdeaCardType;
    isSelected?: boolean;
    isCompact?: boolean;
    isStage?: boolean;
    isDraggable?: boolean;
    isDropTarget?: boolean;
    onSelect?: () => void;
    onUpdate?: (updates: Partial<IdeaCardType>) => void;
    onDelete?: () => void;
    onToggleFocusLock?: () => void;
    onEntityRemove?: (entityId: string) => void;
}

export const IdeaCard: React.FC<IdeaCardProps> = ({
    card,
    isSelected = false,
    isCompact = false,
    isStage = false,
    isDraggable = true,
    isDropTarget = true,
    onSelect,
    onUpdate,
    onDelete,
    onToggleFocusLock,
    onEntityRemove
}) => {
    // Focus config
    const focusConfig = FOCUS_CONFIG[card.focus];
    const FocusIcon = focusConfig.icon;

    // ═══════════════════════════════════════════════════════════════════════════════
    // LOCAL STATE FOR RESPONSIVE EDITING - Notion/Substack pattern
    // ═══════════════════════════════════════════════════════════════════════════════
    const [localTitle, setLocalTitle] = useState(card.title || '');
    const [localContent, setLocalContent] = useState(card.content || '');
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const currentCardIdRef = useRef<string | null>(null);

    // Initialize when card changes
    useEffect(() => {
        if (card.id !== currentCardIdRef.current) {
            setLocalTitle(card.title || '');
            setLocalContent(card.content || '');
            currentCardIdRef.current = card.id;
        }
    }, [card.id]); // Only depend on card ID

    // Debounced save
    const queueSave = useCallback((updates: Partial<IdeaCardType>) => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            onUpdate?.(updates);
        }, 1500); // 1.5 second idle timeout
    }, [onUpdate]);

    // Handlers for title and content
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newTitle = e.target.value;
        setLocalTitle(newTitle);
        queueSave({ title: newTitle });
    };

    const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newContent = e.target.value;
        setLocalContent(newContent);
        queueSave({ content: newContent });
    };

    // Save immediately on blur
    const handleTitleBlur = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        if (localTitle !== card.title) {
            onUpdate?.({ title: localTitle });
        }
    };

    const handleContentBlur = () => {
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        if (localContent !== card.content) {
            onUpdate?.({ content: localContent });
        }
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // Draggable setup (for moving card)
    const {
        attributes: dragAttributes,
        listeners: dragListeners,
        setNodeRef: setDragRef,
        transform,
        isDragging
    } = useDraggable({
        id: `card-${card.id}`,
        data: { type: 'card', card },
        disabled: !isDraggable
    });

    // Droppable setup (for receiving entities)
    const {
        setNodeRef: setDropRef,
        isOver
    } = useDroppable({
        id: `card-drop-${card.id}`,
        data: { type: 'card-drop', cardId: card.id },
        disabled: !isDropTarget
    });

    // Combine refs
    const setNodeRef = (node: HTMLDivElement | null) => {
        setDragRef(node);
        setDropRef(node);
    };

    // Group entities by type for display
    const entityGroups = useMemo(() => {
        const groups: Record<string, AttachedEntity[]> = {
            subject: [],
            location: [],
            object: []
        };
        card.entities.forEach(entity => {
            groups[entity.type].push(entity);
        });
        return groups;
    }, [card.entities]);

    // Instance counts by lane
    const instanceCount = card.instances?.length || 0;
    const macroCount = card.instances?.filter(i => i.lane === 'macro').length || 0;
    const microCount = card.instances?.filter(i => i.lane === 'micro').length || 0;
    const isPlaced = instanceCount > 0;

    const style = {
        transform: CSS.Translate.toString(transform),
        zIndex: isDragging ? 100 : undefined
    };

    // Compact version for card bin / timeline - Premiere Pro style
    if (isCompact) {
        const entityCount = card.entities.length;
        const subjectCount = card.entities.filter(e => e.type === 'subject').length;
        const hasLocation = card.entities.some(e => e.type === 'location');

        return (
            <motion.div
                ref={setNodeRef}
                style={style}
                {...dragAttributes}
                {...dragListeners}
                onClick={onSelect}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className={`
                    px-2.5 py-1.5 rounded-md cursor-pointer transition-all
                    border relative overflow-hidden group bg-zinc-900
                    ${isSelected ? 'border-slate-blue/50 bg-slate-blue/5' : 'border-iron/50 hover:border-iron'}
                    ${isDragging ? 'cursor-grabbing opacity-90' : 'cursor-grab'}
                `}
            >
                {/* Subtle accent line on left */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-0.5 opacity-60"
                    style={{ backgroundColor: focusConfig.accent }}
                />

                <div className="flex items-center gap-2 relative z-10 pl-1.5">
                    {/* Focus Icon - Small, muted */}
                    <div className={`w-4 h-4 rounded flex items-center justify-center ${focusConfig.bg}`}>
                        <FocusIcon className={`w-2.5 h-2.5 ${focusConfig.color}`} weight="fill" />
                    </div>

                    {/* Title */}
                    <span className="text-[11px] font-medium text-neutral-200 truncate max-w-[100px]">
                        {card.title || 'Untitled'}
                    </span>

                    {/* Entity Counts (compact) */}
                    {entityCount > 0 && (
                        <div className="flex items-center gap-1 text-[9px] text-gray-500 ml-auto">
                            {subjectCount > 0 && (
                                <span className="flex items-center bg-graphite px-1 py-0.5 rounded">
                                    <User className="w-2.5 h-2.5" weight="fill" />
                                    {subjectCount > 1 && <span className="ml-0.5">{subjectCount}</span>}
                                </span>
                            )}
                            {hasLocation && <MapPin className="w-2.5 h-2.5 text-place" weight="fill" />}
                        </div>
                    )}
                </div>
            </motion.div>
        );
    }

    // Full card view - Premiere Pro style
    return (
        <motion.div
            ref={setNodeRef}
            style={style}
            onClick={onSelect}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className={`
                relative flex flex-col rounded-md transition-all h-full
                border bg-zinc-900
                ${isSelected ? 'border-slate-blue/40' : 'border-iron/60'}
                ${isDragging ? 'cursor-grabbing scale-[1.02]' : ''}
            `}
        >
            {/* Drag Handle + Header - Premiere toolbar style */}
            <div
                {...dragAttributes}
                {...dragListeners}
                className={`flex items-center gap-2.5 px-3 py-2 border-b border-iron/40 relative z-10 bg-neutral-900 rounded-t-md ${isDraggable ? 'cursor-grab active:cursor-grabbing' : ''}`}
            >
                {/* Focus Indicator - Subtle badge */}
                <div className={`flex items-center gap-1.5 px-2 py-1 rounded ${focusConfig.bg} border ${focusConfig.border}`}>
                    <FocusIcon className={`w-3 h-3 ${focusConfig.color}`} weight="fill" />
                    <span className={`text-[10px] font-medium ${focusConfig.color}`}>
                        {focusConfig.label}
                    </span>
                </div>

                <div className="flex-1" />

                {/* Focus lock toggle */}
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onToggleFocusLock?.();
                    }}
                    className={`p-1 rounded transition-all ${card.focus_locked
                            ? 'text-thing bg-thing/10 hover:bg-thing/15'
                            : 'text-gray-500 hover:text-gray-400 hover:bg-graphite'
                        }`}
                    title={card.focus_locked ? 'Focus locked' : 'Focus auto-inferred'}
                >
                    {card.focus_locked ? <Lock className="w-3 h-3" weight="fill" /> : <LockOpen className="w-3 h-3" />}
                </button>

                {/* Placement chips (Macro/Micro counts) */}
                {isPlaced && (
                    <div className="flex items-center gap-1.5 pl-2 border-l border-iron/40">
                        {macroCount > 0 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-blue" title="Macro Placement" />
                        )}
                        {microCount > 0 && (
                            <div className="w-1.5 h-1.5 rounded-full bg-entity" title="Micro Placement" />
                        )}
                    </div>
                )}

                {/* Menu - Premiere dropdown style */}
                <div className="relative group ml-1">
                    <button
                        onClick={(e) => e.stopPropagation()}
                        className="p-1 rounded text-gray-500 hover:text-gray-400 hover:bg-graphite transition-colors"
                    >
                        <DotsThreeVertical className="w-3.5 h-3.5" weight="bold" />
                    </button>
                    <div className="absolute right-0 top-full mt-1 py-1 bg-zinc-900 border border-iron rounded-md shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-20 min-w-[120px]">
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete?.();
                            }}
                            className="w-full px-3 py-1.5 text-left text-[10px] font-medium text-red-400 hover:bg-red-500/10 flex items-center gap-2 transition-colors"
                        >
                            <Trash className="w-3 h-3" />
                            Delete Card
                        </button>
                    </div>
                </div>
            </div>

            {/* Title - Clean input with local state */}
            <div className="px-3 py-2 relative z-10">
                <input
                    type="text"
                    value={localTitle}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Card title..."
                    className="w-full bg-transparent text-sm font-semibold text-neutral-200 placeholder-gray-500 outline-none"
                />
            </div>

            {/* Content - Subtle textarea with local state */}
            <div className="px-3 pb-3 flex-1 relative z-10">
                <textarea
                    value={localContent}
                    onChange={handleContentChange}
                    onBlur={handleContentBlur}
                    onClick={(e) => e.stopPropagation()}
                    placeholder="Describe your idea..."
                    className="w-full h-28 bg-transparent text-xs text-gray-400 placeholder-gray-500 outline-none resize-none leading-relaxed custom-scrollbar"
                />
            </div>

            {/* Attached Entities - Premiere style footer */}
            <AnimatePresence>
                {card.entities.length > 0 && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="px-3 py-2 border-t border-iron/40 bg-neutral-900 relative z-10 rounded-b-md"
                    >
                        <div className="flex flex-wrap gap-1.5">
                            {/* Subjects (Characters) */}
                            {entityGroups.subject.map(entity => (
                                <EntityChip
                                    key={entity.id}
                                    entity={entity}
                                    onRemove={() => onEntityRemove?.(entity.id)}
                                />
                            ))}
                            {/* Locations */}
                            {entityGroups.location.map(entity => (
                                <EntityChip
                                    key={entity.id}
                                    entity={entity}
                                    onRemove={() => onEntityRemove?.(entity.id)}
                                />
                            ))}
                            {/* Objects (Props) */}
                            {entityGroups.object.map(entity => (
                                <EntityChip
                                    key={entity.id}
                                    entity={entity}
                                    onRemove={() => onEntityRemove?.(entity.id)}
                                />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Drop hint overlay - Premiere style */}
            {isOver && (
                <div className="absolute inset-0 rounded-md bg-slate-blue/10 border border-dashed border-slate-blue/50 flex items-center justify-center pointer-events-none z-50">
                    <div className="px-3 py-1.5 bg-slate-blue text-white text-[10px] font-medium rounded">
                        Release to Attach
                    </div>
                </div>
            )}
        </motion.div>
    );
};

// Helper function to infer focus (exported for use elsewhere)
export function inferCardFocus(entities: AttachedEntity[]): CardFocus {
    const hasLocation = entities.some(e => e.type === 'location');
    const subjects = entities.filter(e => e.type === 'subject');
    const objects = entities.filter(e => e.type === 'object');

    // Priority 1: Any location → Scene
    if (hasLocation) return 'scene';

    // Priority 2: Multiple subjects → Relationship
    if (subjects.length >= 2) return 'relationship';

    // Priority 3: One subject + objects → Prop Significance
    if (subjects.length === 1 && objects.length >= 1) return 'prop_significance';

    // Priority 4: One subject alone → Character
    if (subjects.length === 1) return 'character';

    // Priority 5: Objects only → Prop
    if (objects.length >= 1) return 'prop';

    // Default: Note
    return 'note';
}
